import isEqual from 'lodash/isEqual'
import { round } from 'vend-number'
import { LineItem } from '../models/line-item'

/**
 * Detects whether the editable half of a virtual continuation split differs
 * from the locked half in any way other than quantity.
 *
 * A split is only collapsed back into a single line item when nothing but the
 * quantity changed, so a field missing from the comparison below causes a pair
 * to collapse that should have been preserved. That permanently alters which
 * line items exist and which IDs they carry, and the server has no linkage
 * between the halves with which to detect it.
 *
 * The disposition map is therefore keyed by `keyof LineItem`, so adding a field
 * to `LineItem` without classifying it is a compile error.
 *
 * ## Why price book derived fields need care
 *
 * The two halves of a split are not priced at the same price book quantity.
 * `getTotalQuantityForMatchingLineItems` (`price-book.ts`) skips `confirmed`
 * line items when summing the quantity that `withinQuantityLimits` filters on,
 * and the locked half is confirmed. Once a merchant edit unconfirms the
 * editable half, `_shouldSkipPricingLineItem` (`price-manager.ts`) keeps the
 * locked half at the values resolved for the original full quantity, while the
 * editable half is repriced against an aggregate consisting of only itself.
 *
 * Note that `_shouldSkipPricingLineItem` requires `taxInfo` as well as
 * `confirmed`; being confirmed is not on its own enough to be skipped. That
 * holds for a locked half in production, which is part of a sale loaded from
 * the server and so always carries tax info, but it is easy to violate in a
 * fixture — and a locked half without tax info is repriced alongside the
 * editable half, which hides the divergence between them entirely.
 *
 * Any price book quantity break between the remainder quantity and the original
 * quantity therefore separates the halves, on every edit, without the merchant
 * having changed anything but the quantity. That is why the `displayBestDiscount*`
 * fields below are excluded — and, per LRX-43628, why the loyalty fields are
 * deliberately not.
 *
 * The rule that decides between them: a quantity-break sensitive field is only
 * safe to exclude if it is cosmetic. A field that is persisted and multiplied by
 * quantity is not, because collapsing across a difference silently changes a
 * value the customer receives.
 */

type Normalize = (value: any) => unknown

type FieldDisposition =
  | { kind: 'excluded' }
  | { kind: 'compared'; normalize?: Normalize }

const excluded: FieldDisposition = { kind: 'excluded' }

const compared = (normalize?: Normalize): FieldDisposition => ({
  kind: 'compared',
  normalize,
})

/**
 * Canonicalises a decimal so that '5', '5.00' and 5 compare equal.
 *
 * Rounded to 4dp because the sale calculation rounds per-unit prices and taxes
 * to 5dp (`calculate-line-item.ts`), and the two halves of a split are not
 * guaranteed to round identically: a tax-inclusive 40.00 line at 15% tax
 * yielded a `basePrice` of '34.7826' on one half and '34.78261' on the other.
 * Comparing at the calculation's own precision would treat that as a merchant
 * edit and preserve every split.
 *
 * 4dp absorbs exactly one unit in the last place and nothing more. Merchants
 * edit prices in currency units, so the smallest real change — one cent — is
 * three orders of magnitude larger and still detected.
 *
 * Falls back to a string comparison for values VendNumber cannot parse, which
 * it signals by throwing. This runs on the sale completion path, so an
 * unparseable value must not take the sale down; comparing as strings at worst
 * preserves a split that could have collapsed.
 */
const COMPARISON_DECIMAL_PLACES = 4

const decimal: Normalize = value => {
  if (value === undefined || value === null || value === '') {
    return null
  }

  try {
    return round(value, COMPARISON_DECIMAL_PLACES).toString()
  } catch {
    return String(value)
  }
}

/**
 * Compared by ID. `ApiProduct` is a large object and `lineItemToJSON` stores it
 * as `product_id`, so it is rehydrated as a different object after a reload.
 */
const productId: Normalize = product => product?.id ?? null

/**
 * Compared by tax group ID and rate content. Reference comparison is unsafe
 * because `getTaxInfo` (`line-item.ts`) and `unit-price-manager.ts` can build
 * new objects for an identical tax configuration.
 */
const taxInfo: Normalize = tax =>
  tax
    ? {
        id: tax.id,
        rates: (tax.rates ?? []).map((rate: any) => ({
          id: rate.id,
          rate: decimal(rate.rate),
        })),
      }
    : null

/**
 * Compared by content. The monetary fields are per-unit.
 */
const lineItemPromotion: Normalize = promotion =>
  promotion
    ? {
        id: promotion.id,
        promotion_ids: promotion.promotion_ids ?? [],
        unit_price: decimal(promotion.unit_price),
        unit_discount: decimal(promotion.unit_discount),
        unit_tax: decimal(promotion.unit_tax),
        unit_tax_discount: decimal(promotion.unit_tax_discount),
        loyalty_value: decimal(promotion.loyalty_value),
        explicit_loyalty_value: !!promotion.explicit_loyalty_value,
      }
    : null

const promotionDetails: Normalize = details =>
  Array.isArray(details)
    ? details.map(detail => ({
        amount: decimal(detail.amount),
        description: detail.description ?? null,
        id: detail.id ?? null,
        name: detail.name ?? null,
        promoCode: detail.promoCode ?? null,
        promoCodeId: detail.promoCodeId ?? null,
      }))
    : null

/**
 * Every field on `LineItem`, classified.
 *
 * The default is `compared`. Excluding a field is the dangerous direction: a
 * compared field that differs for a non-merchant reason only preserves the
 * split, which is recoverable, whereas an excluded field that differs for a
 * merchant reason causes an irreversible wrong collapse.
 *
 * Each exclusion below is a field that provably differs between the two halves
 * for reasons the split itself created.
 */
export const LINE_ITEM_FIELD_DISPOSITIONS: Record<
  keyof LineItem,
  FieldDisposition
> = {
  // Identity: the split gives the editable half a fresh localId and no id.
  localId: excluded,
  id: excluded,

  // The quantities are what the split divides, and what merchants may change
  // without preserving the split.
  quantity: excluded,
  displayQuantity: excluded,
  // The split assigns the editable half `originalQuantity: remaining`, so this
  // differs on every split. Comparing it would mean no pair ever collapses.
  originalQuantity: excluded,

  // Quantity-scaled, so they differ whenever the quantity does.
  totals: excluded,
  taxComponents: excluded,

  // The best available price book price, which is selected from only those
  // price book products passing `withinQuantityLimits` (`price-book.ts`). The
  // two halves are filtered at different quantities — see the note on price
  // book derived fields at the top of this file — so a product with a quantity
  // break resolves a different best price for each half, purely because the
  // split gave them different quantities.
  // Safe to exclude because these are strikethrough display hints: never
  // merchant-entered, never persisted, never multiplied by quantity.
  displayBestDiscountPercent: excluded,
  displayBestDiscountPrice: excluded,

  // Lifecycle and split state rather than merchant-visible values.
  confirmed: excluded,
  calculating: excluded,
  isEditableForContinue: excluded,
  continuationEditableLocalId: excluded,
  splitFromLineItemId: excluded,

  // Per-unit pricing. Also quantity-break sensitive, but a differing charged
  // price is a genuine reason to preserve the split, so comparing them is
  // correct rather than merely conservative.
  price: compared(decimal),
  basePrice: compared(decimal),
  tax: compared(decimal),
  baseTax: compared(decimal),
  displayPrice: compared(decimal),
  displayBasePrice: compared(decimal),
  displayDiscount: compared(decimal),
  displayDiscountPercent: compared(decimal),
  displayMarkupPercent: compared(decimal),

  // Loyalty is quantity-break sensitive by the same mechanism as
  // `displayBestDiscount*` above: it comes from the winning price book tuple,
  // either copied from `priceBookProduct.loyalty_value` or derived from that
  // tuple's price (`calculate-line-item.ts`). `calculateBestPriceBookInfo`
  // picks the winner by lowest price with the loyalty value as the tie-breaker,
  // so where two tuples tie on price these fields are the only ones that
  // differ; where they do not tie, `price` differs too and preserves the split
  // anyway.
  //
  // They are compared regardless, unlike `displayBestDiscount*`, because they
  // are not cosmetic. `loyaltyValue` is persisted per line item
  // (`sale-transformer.ts`) and accrual is `sum(quantity * loyaltyValue)`
  // (`loyalty-manager.ts`). Excluding them would let a pair collapse onto the
  // locked half's rate applied to the combined quantity, changing the points
  // the customer earns — irreversibly, and with no linkage the server could use
  // to detect it.
  //
  // Accepted consequence: a loyalty price book with a quantity break, where the
  // tuples tie on price, preserves the split rather than collapsing it. That is
  // today's behaviour and is the safe outcome. Audited under LRX-43628.
  loyaltyValue: compared(decimal),
  explicitLoyaltyValue: compared(),

  // Objects needing more than referential equality.
  product: compared(productId),
  taxInfo: compared(taxInfo),
  // Promotions are quantity-sensitive too: the line item quantity is sent to
  // `/api/2.0/discount` (`sale-promotion-resource.ts`), so eligibility can
  // change with it. Compared anyway, because the design already elects to
  // preserve the split on any promotion-driven repricing. Audited under
  // LRX-43628.
  lineItemPromotion: compared(lineItemPromotion),
  promotionDetails: compared(promotionDetails),

  // Merchant-editable values.
  note: compared(),
  salesperson: compared(),
  customFields: compared(),
  fulfilmentType: compared(),
  fulfilmentOutletId: compared(),
  adjusted: compared(),
  customerTaxApplied: compared(),

  // Spread unchanged by the split, or stamped once at line item creation, so
  // they are identical between the halves and free to compare.
  isReturn: compared(),
  giftCardNumber: compared(),
  giftCardReload: compared(),
  giftCardClientId: compared(),
  giftCardExpiresAt: compared(),
  dateAdded: compared(),
  registerId: compared(),
  registerSequenceId: compared(),
  // The split overwrites this with `true` on the locked half; see
  // LOCKED_HALF_OVERRIDES below.
  fulfilmentCompleted: compared(),
}

/**
 * The pre-split values of fields the split overwrites on the locked half.
 *
 * `applyUnfulfilledSaleEditability` only splits a line item whose
 * `fulfilmentCompleted` is false, so the pre-split value is always false.
 */
const LOCKED_HALF_OVERRIDES: Partial<LineItem> = {
  fulfilmentCompleted: false,
}

const valueOf = (lineItem: LineItem, key: keyof LineItem) =>
  key in LOCKED_HALF_OVERRIDES ? LOCKED_HALF_OVERRIDES[key] : lineItem[key]

/**
 * True when the editable half differs from the locked half in any compared
 * field, meaning the split must be preserved rather than collapsed.
 *
 * The locked half doubles as the pre-split snapshot: the split copies the
 * original line item and overrides only the quantities and
 * `fulfilmentCompleted`, the latter being substituted above.
 */
export const hasNonQuantityChange = (
  locked: LineItem,
  editable: LineItem
): boolean =>
  (Object.keys(LINE_ITEM_FIELD_DISPOSITIONS) as Array<keyof LineItem>).some(
    key => {
      const disposition = LINE_ITEM_FIELD_DISPOSITIONS[key]

      if (disposition.kind === 'excluded') {
        return false
      }

      const normalize = disposition.normalize ?? ((value: unknown) => value)

      return !isEqual(normalize(valueOf(locked, key)), normalize(editable[key]))
    }
  )
